import { DISPO_FULL, DISPO_HALF } from './grilleReponses';

/**
 * Taille NATURELLE des SVG des générateurs (data URL) — standard papier.
 *
 * Avant (2026-09-02), .ep-enonce-img étirait toute image à 70 % de la
 * pleine largeur (436 px) ou à 100 % de la demi-colonne (271 px), quelle que
 * soit sa taille d'origine : un cadran dessiné en 200 px s'imprimait en
 * 11,5 cm (×2,2) avec des chiffres de 37 px, un disque de 140 px mangeait
 * toute la demi-colonne (constat utilisateur, 43 cartes de 6e sur 57
 * agrandies au-delà de leur taille). À l'inverse, un SVG large (610 px)
 * écrasé en demi-colonne tombait à ×0,44 avec des étiquettes de 4 px.
 *
 * Deux règles, décidées AVANT le rendu à partir du SVG lui-même :
 *   1. ne jamais agrandir : la largeur naturelle (viewBox / width) est un
 *      plafond, posé sur l'<img> via --ep-svg-w (cf. .ep-enonce-img) ;
 *   2. pas de demi-colonne pour un SVG large : au-delà de SEUIL_LARGEUR_DEMI,
 *      ou si sa plus petite police passerait sous POLICE_MIN_PAPIER une fois
 *      réduit à la demi-colonne, le bloc exige la pleine largeur
 *      (getBlockDensity).
 * Les images non SVG (uploads prof, PNG) gardent le comportement historique :
 * aucune dimension connue → pas de plafond.
 */

/** Largeur naturelle au-delà de laquelle la demi-colonne réduirait sous ×0,8. */
export const SEUIL_LARGEUR_DEMI = 340;
/** Plus petite police lisible sur papier (px CSS ≈ 6 pt). */
export const POLICE_MIN_PAPIER = 8;

const cache = new Map();

const decoderDataUrl = (src) => {
    if (typeof src !== 'string' || !src.startsWith('data:image/svg+xml')) return null;
    const virgule = src.indexOf(',');
    if (virgule < 0) return null;
    const entete = src.slice(0, virgule);
    const corps = src.slice(virgule + 1);
    try {
        if (/;base64/i.test(entete)) {
            if (typeof atob === 'function') {
                // atob rend du latin-1 : on repasse par l'UTF-8 pour les accents.
                const bin = atob(corps);
                const octets = Uint8Array.from(bin, (c) => c.charCodeAt(0));
                return new TextDecoder('utf-8').decode(octets);
            }
            return Buffer.from(corps, 'base64').toString('utf8');
        }
        return decodeURIComponent(corps);
    } catch {
        return null;
    }
};

const nombre = (s) => {
    const n = parseFloat(s);
    return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * @returns {{ w: number, h: number, fontMin: number|null } | null}
 *   w/h en px CSS (attributs width/height, sinon viewBox) ; fontMin = plus
 *   petite font-size trouvée (attribut ou style), null si aucune.
 */
export const dimensionsSvg = (src) => {
    if (typeof src !== 'string' || !src.startsWith('data:image/svg+xml')) return null;
    if (cache.has(src)) return cache.get(src);
    let res = null;
    const svg = decoderDataUrl(src);
    if (svg) {
        const ouverture = (svg.match(/<svg\b[^>]*>/i) || [''])[0];
        const vb = ouverture.match(/viewBox\s*=\s*["']\s*[-\d.]+[\s,]+[-\d.]+[\s,]+([\d.]+)[\s,]+([\d.]+)\s*["']/i);
        const wAttr = ouverture.match(/\swidth\s*=\s*["']([\d.]+)(?:px)?["']/i);
        const hAttr = ouverture.match(/\sheight\s*=\s*["']([\d.]+)(?:px)?["']/i);
        const w = (wAttr && nombre(wAttr[1])) || (vb && nombre(vb[1]));
        // Sans height explicite, le ratio du viewBox donne la hauteur.
        const h = (hAttr && nombre(hAttr[1]))
            || (vb && w && nombre(vb[2]) ? (w / nombre(vb[1])) * nombre(vb[2]) : null);
        if (w && h) {
            const polices = [...svg.matchAll(/font-size\s*[=:]\s*["']?\s*([\d.]+)/gi)]
                .map((m) => nombre(m[1])).filter(Boolean);
            res = { w, h, fontMin: polices.length ? Math.min(...polices) : null };
        }
    }
    cache.set(src, res);
    return res;
};

/** Source d'un élément image (imageUrl, sinon imageData déjà en data URL). */
export const srcImage = (el) => {
    const c = el?.config || {};
    if (c.imageUrl) return c.imageUrl;
    if (c.imageData && String(c.imageData).startsWith('data:')) return c.imageData;
    return null;
};

/** Largeur CSS (px) qu'aurait l'image SANS plafond, selon le hint écran
 *  (cf. .ep-enonce-img et ses remaps) — miroir de la feuille de style. */
const largeurCss = (hint, halfWidth) => {
    if (halfWidth) return DISPO_HALF;
    const clamp = (min, pct) => Math.min(DISPO_FULL, Math.max(min, pct * DISPO_FULL));
    if (hint === 'large') return DISPO_FULL;
    if (hint === 'medium') return clamp(280, 0.75);
    if (hint === 'compact' || hint === 'small') return clamp(220, 0.55);
    return clamp(260, 0.7);
};

/** Largeur imprimée (px) d'une image d'énoncé : plafond naturel si SVG. */
export const largeurImprimee = (el, halfWidth) => {
    const css = largeurCss(el?.config?.maxWidth, halfWidth);
    const dims = dimensionsSvg(srcImage(el));
    return dims ? Math.min(css, dims.w) : css;
};

/** Hauteur imprimée (px) d'une image d'énoncé, ou null si inconnue (non SVG). */
export const hauteurImprimee = (el, halfWidth) => {
    const dims = dimensionsSvg(srcImage(el));
    if (!dims) return null;
    return Math.round((largeurImprimee(el, halfWidth) / dims.w) * dims.h);
};

/** Un SVG qui, en demi-colonne, serait réduit sous ×0,8 ou dont la plus petite
 *  police passerait sous POLICE_MIN_PAPIER : le bloc exige la pleine largeur. */
export const svgExigePleineLargeur = (el) => {
    if (el?.type !== 'image') return false;
    const dims = dimensionsSvg(srcImage(el));
    if (!dims) return false;
    if (dims.w > SEUIL_LARGEUR_DEMI) return true;
    const echelleDemi = Math.min(1, DISPO_HALF / dims.w);
    return dims.fontMin != null && dims.fontMin * echelleDemi < POLICE_MIN_PAPIER;
};

/** Vrai si l'un des éléments d'un payload exige la pleine largeur. */
export const payloadExigePleineLargeur = (payload) =>
    (payload?.elements_additionnels?.elements || []).some(svgExigePleineLargeur);
